import { inject, Injectable } from '@angular/core';
import { IAppRequest } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { IFilter } from '@app/modules/form-filter/form-filter.interface';
import { FormFilterService } from '@app/modules/form-filter/form-filter.service';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { FuiDestroyService } from '@services/destroy.service';
import { ENotifyTypes, NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

/**
 * Сервис для управления сохраненными поисками
 */
@Injectable()
export class FormFilterSavedSearchesService {
    readonly #destroy$ = inject(FuiDestroyService);
    readonly #dialogConfirm = inject(AppDialogService);
    readonly #filterService = inject(FormFilterService);
    readonly #appService = inject(AppService);
    readonly #spinner = inject(NgxSpinnerService);
    readonly #notifier = inject(NotifierService);

    /**
     * Получить поток с количеством записей filterRecords
     */
    public get filterRecordsLength$(): Observable<number> {
        return this.#extendFilterRecords$.pipe(map((arr) => arr.length));
    }

    /**
     * Получить текущее количество записей filterRecords
     */
    public get filterRecordsLength() {
        return this.#extendFilterRecords$.value.length;
    }

    /**
     * Получить поток значений filterRecords
     */
    public get filterRecords$() {
        return this.#extendFilterRecords$.asObservable();
    }

    /**
     * Получить текущее значение filterRecords
     */
    public get filterRecords() {
        return this.#extendFilterRecords$.value;
    }

    /**
     * Передать массив записей в фильтр
     * @param arr
     */
    public set filterRecords(arr: any[]) {
        if (Array.isArray(arr)) {
            this.#extendFilterRecords$.next(arr);
        }
    }

    public get selectedFilterId$() {
        return this.#selectedFilterId$.asObservable();
    }

    public get selectedFilterId() {
        return this.#selectedFilterId$.value;
    }

    public set selectedFilterId(id: string) {
        this.#selectedFilterId$.next(id);
    }

    /**
     * Записи в фильтре
     * @private
     */
    readonly #extendFilterRecords$: BehaviorSubject<any[]> = new BehaviorSubject([]);

    /**
     * ID текущего поиска
     * @private
     */
    readonly #selectedFilterId$: BehaviorSubject<string> = new BehaviorSubject(null);

    /**
     * Выбор сохраненного поиска
     * @param filter
     */
    public select(filter: IFilter) {
        if (filter) {
            this.#selectedFilterId$.next(String(filter.id));
        } else {
            this.#selectedFilterId$.next(null);
        }
    }

    /**
     * Удаление сохраненного поиска
     * @param event событие клика
     * @param filter сохраненный поиск
     */
    deleteFilterRecord(event: PointerEvent, filterRecord: IFilter) {
        event.stopPropagation();
        this.#dialogConfirm
            .confirm(
                this.#appService.language_dictionary.system_attention,
                this.#appService.language_dictionary.searchDeleteQueryConfirm.replace('%s', filterRecord.name.ru),
            )
            .pipe(
                filter((res) => res),
                switchMap(() => {
                    this.#spinner.show();
                    return this.#filterService.removeFilter(filterRecord.id);
                }),
                finalize(() => this.#spinner.hide()),
                takeUntil(this.#destroy$),
            )
            .subscribe({
                next: (result: IAppRequest) => {
                    if (result && !result.is_error) {
                        this.#notifier.notify(
                            ENotifyTypes.success,
                            this.#appService.language_dictionary.system_data_successfully_deleted,
                        );
                        this.filterRecords = this.filterRecords.filter((record) => record.id !== filterRecord.id);
                    } else {
                        this.#notifier.notify(ENotifyTypes.error, result.message);
                    }
                },
                error: () => {
                    this.#notifier.notify(
                        ENotifyTypes.error,
                        this.#appService.language_dictionary.system_no_connection_with_system,
                    );
                },
            });
    }
}
